<script setup lang="ts">
import { onMounted, ref, StyleValue, computed, onBeforeUnmount, watch, onBeforeMount, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import ControlPageTab from '@/corePagesModule/components/ControlPageTab.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useInventoryStore } from './useInventoryStore/';
import Pagination from '@/corePagesModule/components/Pagination.vue';
import FilterModalButton from '@/corePagesModule/filterModal/FilterModalButton.vue';
import { MIN_COLUMN_WIDTH } from '@/corePagesModule/BaseTableContent/constants';
import { InventoryTableViewModel } from './inventoryTableViewModel';
import ToggleSwitch from '@/corePagesModule/components/ToggleSwitch.vue';
import WorkDaysCalendarNotYetEnteredAlertModal from '@/components/modal/WorkDaysCalendarNotYetEnteredAlertModal.vue';
import { shouldShowWorkDaysCalendarNotEnteredAlertModal } from '@/appLogic/importData/calendar';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { isLoading, isZeroMatch, isSuccess } from '@/coreDomainsModule/uiLoadingState';
import CreateGroupModal from '@/corePagesModule/group/components/createGroup/CreateGroupModal.vue';
import AddAggregateBusinessGroupModal from '@/corePagesModule/group/components/addAggregateBusinessGroup/AddAggregateBusinessGroupModal.vue';
import { diContainer } from '@/coreContainer/container';
import EditGroupModal from '@/corePagesModule/group/components/editGroup/EditGroupModal.vue';
import DeleteGroupModal from '@/corePagesModule/group/components/deleteGroup/DeleteGroupModal.vue';
import UpdateGroupNameModal from '@/corePagesModule/group/components/editGroup/UpdateGroupNameModal.vue';
import GroupMenuDropdown from '../components/GroupMenuDropdown.vue';
import GroupListSelectInput from '@/corePagesModule/group/components/GroupListSelectInput.vue';
import { GroupMenuService } from '../domains/services/groupMenuService';
import MoveWarehouseModal from '../components/MoveWarehouseModal.vue';
import VirtualizedEzInventoryTable from '../components/InventoryTable/VirtualizedEzInventoryTable.vue';
import VirtualizedEzOrderTable from '../components/InventoryTable/VirtualizedEzOrderTable.vue';
import UpdateBusinessModals from '@/corePagesModule/updateBusinessModal/UpdateBusinessModals.vue';
import SelectDisplayInventoryYearMonthRangeModal from '../components/SelectDisplayInventoryYearMonthRangeModal.vue';
import inventoryInfoControlMenu from '../components/inventoryInfoControlMenu.vue';
import EditUnpaidOrderedOrderModal from '@/corePagesModule/components/EditUnpaidOrderedOrderModal.vue';
import { useRouter } from 'vue-router';
import CombinedOrderSettingModal from '../components/CombineOrder/CombinedOrderSettingModal.vue';
import { ICombinedOrderConditions } from '../domains/entities/CombinedOrder';
import { CombinedOrderService } from '../domains/services/combinedOrderService';
import { IInventoryOrder, IInventoryOrderInner } from '../domains/entities/InventoryOrder';
import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { setTemplateFilterState } from './useInventoryStore/inventoryTemplate';
import { RecommendOrderFactory } from '../domains/factories/RecommendOrderFactory';
import OrderBasesModal from '@/corePagesModule/components/OrderBasis/OrderBasesModal.vue';

// 事前準備（DIコンテナ経由でRepositoryを取得）
// 直接インスタンス化は禁止 - diContainerを使用
const inventoryStore = useInventoryStore();
const { state } = storeToRefs(inventoryStore);
const {
  $reset,
  initTableColumnsWidth,
  onChangePaging,
  fetchState,
  fetchStateWithOpeningChildBusiness,
  switchTableMode,
  closeWorkDaysCalendarNotYetEnteredAlertModal,
  onAppliedFilterModal,
  onSelectedGroupMenu,
  onClosedGroupActionModal,
  onAppliedCreateAggregateBusinessGroup,
  onAppliedAddAggregateBusinessGroup,
  onAppliedEditAggregateBusinessGroup,
  onAppliedDeleteAggregateBusinessGroup,
  onAppliedUpdateNameAggregateBusinessGroup,
  onSwitchedEditTargetGroup,
  onClickedDeleteFromGroupButton,
  onBackToNormalMode,
  resetTableBusinessRowMenuState,
  putEditTransaction,
  setSelectDisplayInventoryYearMonthRangeModalState,
  setInventoryControlMenuStateAndProcessing,
  getFilterItemsValue,
  getFreeColumnFilterItemsValue,
  updateUnpaidOrderedOrder,
} = inventoryStore;

//i18n
const t = i18n.t;

// Router
const router = useRouter();

const inventoryVm = new InventoryTableViewModel();

const tableContainerWidth = ref(0); // ローディングUIの位置を調整するためにコンテナの幅を取得する
const tableContainerHeight = ref(1000); // コンテナの高さを取得する

const isEllipsis = ref(false);

const isRequiredReloadGroups = computed(() => {
  return GroupMenuService.needReloadOnAfterAction(state.value.groupLoadingState.type);
});
const orderBasesModalAggregateBusinessId = computed(() =>
  state.value.businessRowMenuState.orderBasis.modalState ? state.value.businessRowMenuState.businessId : ''
);

const combinedOrderDefaultDesiredDeliveryDate = computed(() => {
  const earliestDate = Object.values(state.value.orderEditData)
    .flatMap((inner) => Object.values(inner))
    .filter((order) => order.status === OrderStatus.Recommended)
    .reduce(
      (acc, order) => {
        if (!order.crossDate) return acc;
        return DateService.compareDates(order.crossDate, acc) === -1 ? order.crossDate : acc;
      },
      { year: 9999, month: 12, day: 31 }
    );
  if (DateService.compareDates(earliestDate, { year: 9999, month: 12, day: 31 }) === 0) {
    return null;
  } else if (DateService.compareDates(earliestDate, DateService.convertDateToIDate(new Date())) !== 1) {
    const nowDate = new Date();
    return DateService.getShiftedIYearMonthByDays(nowDate.getFullYear(), nowDate.getMonth() + 1, nowDate.getDate(), 1);
  } else {
    return earliestDate;
  }
});

/**
 * テキストにマウスオーバーしたときに、テキストが省略されているかどうかを判定する
 * @param e - イベント
 */
const mouseoverTextLabel = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  isEllipsis.value = target.offsetWidth < target.scrollWidth;
};

/**
 * アラートを閉じる処理
 */
const closeAlert = () => {
  state.value.isOpeningAlertBusinessId = '';
};

/**
 * テーブルコンテナのサイズを更新する関数
 */
const updateTableContainerSize = () => {
  const container = document.getElementById('inventory-control');
  if (container) {
    tableContainerWidth.value = container.clientWidth;
    tableContainerHeight.value = state.value.businessRowMenuState.editTransaction.isEditTransactionMode
      ? container.clientHeight - 52
      : container.clientHeight;
    console.log('Table container size updated:', {
      width: tableContainerWidth.value,
      height: tableContainerHeight.value,
    });
  }
};

onBeforeMount(() => {
  // state変数の初期化
  // NOTE: ここで初期化しないと、コンポーネントのマウント時までにstateの表示年月範囲設定が反映されないため
  inventoryStore.$reset();
});

onMounted(async () => {
  state.value.tableLoadingState.status.type = 'loading';
  updateTableContainerSize();
  state.value.workDaysCalendarNotYetEnteredAlertModalStatus = await shouldShowWorkDaysCalendarNotEnteredAlertModal();
  window.addEventListener('scroll', closeAlert);
  window.addEventListener('resize', updateTableContainerSize);
  await initTableColumnsWidth();
  fetchState();
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', closeAlert);
  window.removeEventListener('resize', updateTableContainerSize);
});

watch(
  () => state.value.tableLoadingState.status,
  (newVal) => {
    if (isSuccess(newVal)) {
      const target = document.getElementById('inventory-control');
      if (!target) return;
      let leftScroll = MIN_COLUMN_WIDTH * 2; // アラートの分
      inventoryVm.transactionKeys.forEach((key) => {
        if (key === 'nowInventory') return; //現在庫はスキップ
        leftScroll +=
          (state.value.tableInfos[inventoryVm.getArrivalHeaderKey(key, -1)] ?? MIN_COLUMN_WIDTH) + //現状意図的に大きくしない限りMIN_COLUMN_WIDTHより大きくなることはない
          (state.value.tableInfos[inventoryVm.getArrivalHeaderKey(key, -2)] ?? MIN_COLUMN_WIDTH);
      });
      target.scroll(leftScroll, 0);
    }
  }
);

watch(
  () => state.value.businessRowMenuState.editTransaction.isEditTransactionMode,
  () => {
    updateTableContainerSize();
  }
);

const tableScrollInhibitStyle = computed((): StyleValue => {
  return isSuccess(state.value.tableLoadingState.status) || !state.value.businessRowMenuState.editTransaction.isEditTransactionMode
    ? {}
    : { overflow: 'hidden' };
});

const tableFlowStyle = computed((): StyleValue => {
  return state.value.businessRowMenuState.editTransaction.isEditTransactionMode ? { zIndex: 2 } : null;
});
/**
 * 在庫管理ページの操作メニュー選択ハンドラー
 * @param e - 選択された項目
 */
const handleInventoryControlMenuSelect = async (e: 'excelExport' | 'editDisplayRange' | 'combinedOrderSetting') => {
  if (e === 'excelExport') {
    // フィルター情報をテンプレートページに渡す（型変換はUseCaseで行う）
    const filterItems = getFilterItemsValue().value;
    setTemplateFilterState(filterItems && filterItems.length > 0 ? filterItems : undefined);
    // CSVエクスポートの場合は、テンプレート設定ページにリダイレクト
    router.push({ name: 'inventory-template' });
    return;
  }
  // それ以外は既存の処理を実行
  await setInventoryControlMenuStateAndProcessing(e);
};

/**
 * まとめ発注案作成ボタン押下時の処理
 * @param combinedOrderSetting - まとめ発注設定
 */
const onConfirmCombinedOrder = async (combinedOrderSetting: ICombinedOrderConditions) => {
  /**
   * orderEditDataのフォールバック関数
   */
  const _makeFallBack = () =>
    RecommendOrderFactory.create({
      amount: NaN,
      crossDate: DateService.convertDateToIDate(new Date()),
    });

  state.value.combinedOrdersState.settingModalStatus = 'progress';

  // 処理が長いとフリーズしているように見えるので、setTimeoutでレンダリング待ってから処理実行
  const calcedCombinedOrder = await new Promise<{
    maxTotalAmount: number;
    data: IInventoryOrder;
  } | null>((resolve) => {
    setTimeout(() => {
      resolve(
        CombinedOrderService.calculateCombinedOrder(
          combinedOrderSetting,
          state.value.summaryData,
          state.value.businessList,
          state.value.monthlyData
        )
      );
    }, 0);
  });

  state.value.combinedOrdersState.settingModalStatus = 'close';

  if (calcedCombinedOrder !== null) {
    const createdOrderEditData: IInventoryOrder = {};
    for (const key of Object.keys(state.value.orderEditData)) {
      const inner = state.value.orderEditData[key];
      const createdOrderEditDataInner: Record<number, IInventoryOrderInner> = {};
      for (const i of Object.keys(inner)) {
        const iNum = Number(i);
        createdOrderEditDataInner[iNum] = calcedCombinedOrder.data[key]?.[iNum] ?? _makeFallBack();
      }
      createdOrderEditData[key] = createdOrderEditDataInner;
    }

    state.value.orderEditData = createdOrderEditData;
    state.value.combinedOrdersState.combinedOrderContents.maxTotalAmount = calcedCombinedOrder.maxTotalAmount;
    state.value.checkedAggregateBusinessIds = Object.keys(calcedCombinedOrder.data);
  }
};
</script>

<template>
  <div v-show="state.businessRowMenuState.editTransaction.isEditTransactionMode" class="edit-transaction-control__overlay" />
  <div class="inventory-control__page-wrap">
    <div class="inventory-header__title">{{ t('管理表') }}</div>
    <div class="inventory-header__wrap">
      <ControlPageTab class="inventory-tabs" />
      <div class="inventory-tags">
        <div class="inventory-tag inventory-tag--approval-requested">
          <p>{{ t('推奨') }}</p>
        </div>
        <div class="status-dot status-dot--approval-requested" />
        <div class="inventory-tag inventory-tag--recommended">
          <p>{{ t('承認依頼') }}</p>
        </div>
        <div class="status-dot status-dot--recommended" />
        <div class="inventory-tag inventory-tag--unpaid">
          <p>{{ t('未納') }}</p>
        </div>
        <div class="status-dot status-dot--unpaid" />
      </div>
      <div class="inventory-header__right-content">
        <TextButton v-if="state.pageMode === 'order'" @click="state.combinedOrdersState.settingModalStatus = 'generate'">
          {{ t('まとめ発注案の生成') }}
        </TextButton>
        <ToggleSwitch
          v-if="state.pageMode !== 'editGroup'"
          class="inventory-header__toggle"
          :is-enabled="state.pageMode === 'order'"
          @on-toggled="switchTableMode"
        >
          {{ t('発注を表示する') }}
        </ToggleSwitch>
        <inventoryInfoControlMenu
          class="inventory-header__info-control-menu"
          :tool-tip-message="undefined"
          :disabled="state.businessRowMenuState.editTransaction.isEditTransactionMode"
          @select="handleInventoryControlMenuSelect($event)"
        />
      </div>
    </div>
    <div class="inventory-header__wrap">
      <div class="inventory-header__content">
        <FilterModalButton
          filter-modal-type="inventory"
          :select-input-index="5"
          :filter-items="getFilterItemsValue().value"
          :free-column-filter-items="getFreeColumnFilterItemsValue().value"
          @apply="onAppliedFilterModal"
        />
        <template v-if="state.pageMode === 'editGroup'">
          <GroupListSelectInput
            class="edit-group__select-input"
            :select-input-index="1"
            :store-tag="'editGroupModeInventoryControl'"
            :selected-group-id-prop="state.editGroupModeState.selectedEditGroupId"
            :reload="isRequiredReloadGroups"
            :use-reset="false"
            use-teleport
            @selected-group="(groupId) => onSwitchedEditTargetGroup(groupId)"
          />
          <TextButton
            class="edit-group__update-name-button"
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :disabled="state.editGroupModeState.selectedEditGroupId === ''"
            @click="() => (state.editGroupModeState.updateGroupNameModalState = true)"
          >
            {{ t('グループ名を編集') }}
          </TextButton>
          <TextButton
            class="edit-group__delete-from-group-button"
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :disabled="state.checkedAggregateBusinessIds.length === 0"
            @click="() => onClickedDeleteFromGroupButton(diContainer.groupRepository)"
          >
            {{ t('グループから外す') }}
          </TextButton>
          <TextButton class="edit-group__cancel-button" :button-style="'btn-cancel'" :size="'btn-md'" @click="() => onBackToNormalMode()">
            {{ t('元のページに戻る') }}
          </TextButton>
        </template>
        <GroupMenuDropdown
          v-else
          :is-selected-businesses="state.checkedAggregateBusinessIds.length > 0"
          :is-open-prop="state.isOpenGroupMenuDropdown"
          :selected-item-prop="state.selectedGroupMenu"
          @click:select-input="() => (state.isOpenGroupMenuDropdown = true)"
          @close="() => (state.isOpenGroupMenuDropdown = false)"
          @selected-item="
            (item) => {
              onSelectedGroupMenu(item);
            }
          "
        />
      </div>
      <Pagination
        v-if="state.pagingOptions"
        class="inventory-header__pagination"
        :paging-options="state.pagingOptions"
        :is-loading="isLoading(state.tableLoadingState.status)"
        is-inventory-page
        @on-change-page="onChangePaging($event)"
      />
    </div>
    <div id="inventory-control" class="inventory-control base-table-wrapper" :style="tableFlowStyle" @scroll="closeAlert">
      <div v-if="state.businessRowMenuState.editTransaction.isEditTransactionMode" class="edit-transaction-control">
        <div class="edit-transaction-control__text">{{ t('数値データ編集中') }}</div>
        <div class="edit-transaction-control__button">
          <TextButton size="btn-sm" @click="putEditTransaction">{{ t('保存') }}</TextButton>
          <TextButton
            button-style="btn-secondary"
            size="btn-sm"
            @click="
              () => {
                $reset();
                fetchState();
              }
            "
            >{{ t('キャンセル') }}</TextButton
          >
        </div>
      </div>
      <div class="" :style="tableScrollInhibitStyle">
        <!-- 通常モード/グループ編集モードテーブル -->
        <VirtualizedEzInventoryTable
          v-if="state.pageMode !== 'order'"
          :table-container-width="tableContainerWidth"
          :table-container-height="tableContainerHeight"
          :is-ellipsis="isEllipsis"
          :transaction-months="state.transactionMonths"
          :mouseover-text-label="mouseoverTextLabel"
        />
        <!-- 発注モードテーブルはVirtualizedEzOrderTableを利用 -->
        <VirtualizedEzOrderTable
          v-else
          :table-container-width="tableContainerWidth"
          :table-container-height="tableContainerHeight"
          :is-ellipsis="isEllipsis"
          :transaction-months="state.transactionMonths"
          :mouseover-text-label="mouseoverTextLabel"
        />
        <InfoModal :modal-state="isLoading(state.editOrderInfoLoadingState.status)" icon="loading" :enable-outside-click="false">
          <template #title>{{ t('データを保存しています') }}</template>
        </InfoModal>
        <InfoModal :modal-state="isLoading(state.editMemoLoadingState.state.status)" icon="loading" :enable-outside-click="false">
          <template #title>{{ state.editMemoLoadingState.isDelete ? t('メモを削除しています') : t('メモを保存しています') }}</template>
        </InfoModal>
        <InfoModal
          :modal-state="isZeroMatch(state.editMemoLoadingState.state.status)"
          icon="error"
          color-style="error"
          back-button-label="OK"
          :enable-outside-click="false"
          @on-back="
            () => {
              state.editMemoLoadingState = {
                state: {
                  status: {
                    type: 'none',
                  },
                },
                dataType: 'none',
                isDelete: false,
              };
              state.isOpeningBusinessId = '';
              fetchState();
              return;
            }
          "
        >
          <template #title>
            {{ t('エラーが発生しました') }}
          </template>
          <template #description>
            {{
              state.editMemoLoadingState.dataType === 'aggregateBusiness'
                ? t('指定された集計情報は未登録か、すでに削除されています。')
                : t('指定された取引情報は未登録か、すでに削除されています。')
            }}<br />
            {{ t('OKボタンを押すとテーブルを更新します。') }}
          </template>
        </InfoModal>
      </div>
    </div>
    <!-- グループ作成/削除等用のローディング -->
    <InfoModal :modal-state="isLoading(state.groupLoadingState.state.status)" icon="loading" :enable-outside-click="false">
      <template #title>{{ GroupMenuService.getLoadingText(state.groupLoadingState.type) }}</template>
    </InfoModal>
    <WorkDaysCalendarNotYetEnteredAlertModal
      :is-open-modal="state.workDaysCalendarNotYetEnteredAlertModalStatus"
      @outside-click="closeWorkDaysCalendarNotYetEnteredAlertModal"
    />
    <!-- グループ新規作成モーダル -->
    <CreateGroupModal
      :modal-state="state.selectedGroupMenu === 'CreateGroup'"
      @close="onClosedGroupActionModal"
      @apply="
        (inputText) => {
          onAppliedCreateAggregateBusinessGroup(diContainer.groupRepository, inputText);
        }
      "
    />
    <!-- グループへ集計追加モーダル -->
    <AddAggregateBusinessGroupModal
      :modal-state="state.selectedGroupMenu === 'AddBusinessToGroup'"
      :select-input-index="1"
      @close="onClosedGroupActionModal"
      @apply="
        (selectedGroupId) => {
          onAppliedAddAggregateBusinessGroup(diContainer.groupRepository, selectedGroupId);
        }
      "
    />
    <!-- グループ編集へ入るモーダル -->
    <EditGroupModal
      :modal-state="state.selectedGroupMenu === 'EditGroup'"
      :select-input-index="2"
      @close="onClosedGroupActionModal"
      @apply="
        (selectedGroupId, selectedGroupName) => {
          onAppliedEditAggregateBusinessGroup(diContainer.inventoryRepository, selectedGroupId, selectedGroupName);
        }
      "
    />
    <!-- グループ削除モーダル -->
    <DeleteGroupModal
      :modal-state="state.selectedGroupMenu === 'DeleteGroup'"
      :select-input-index="3"
      @close="onClosedGroupActionModal"
      @apply="
        (selectedGroupId) => {
          onAppliedDeleteAggregateBusinessGroup(diContainer.inventoryRepository, diContainer.groupRepository, selectedGroupId);
        }
      "
    />
    <!-- グループの名称変更モーダル -->
    <UpdateGroupNameModal
      :modal-state="state.editGroupModeState.updateGroupNameModalState"
      :select-input-index="4"
      :target-group-id="state.editGroupModeState.selectedEditGroupId"
      :current-group-name="state.editGroupModeState.selectedEditGroupName"
      @close="() => (state.editGroupModeState.updateGroupNameModalState = false)"
      @apply="
        (inputText) => {
          onAppliedUpdateNameAggregateBusinessGroup(diContainer.groupRepository, inputText);
        }
      "
    />
    <!-- 置き場移動モーダル -->
    <MoveWarehouseModal />
    <!-- 置き場移動 ローディングモーダル　-->
    <InfoModal :modal-state="state.moveWarehouseModalState.isLoadingModalOpen" icon="loading" :enable-outside-click="false">
      <template #title>{{ t('ローディング中') }}</template>
    </InfoModal>
    <!-- 置き場移動 登録完了モーダル -->
    <InfoModal
      :modal-state="state.moveWarehouseModalState.isSuccessModalOpen"
      :back-button-label="t('閉じる')"
      icon="info"
      enable-outside-click
      @on-back="state.moveWarehouseModalState.isSuccessModalOpen = false"
    >
      <template #title>{{ t('置場移動の登録を完了しました。') }}</template>
    </InfoModal>

    <UpdateBusinessModals
      :is-opening-aggregate-business-edit="
        state.businessRowMenuState.edit.isEditingModalOpen && state.businessRowMenuState.businessType === 'aggregateBusiness'
      "
      :is-opening-base-business-edit="
        state.businessRowMenuState.edit.isEditingModalOpen && state.businessRowMenuState.businessType === 'baseBusiness'
      "
      :is-create-mode="state.businessRowMenuState.edit.isCreateMode"
      :item="state.businessRowMenuState.item"
      @close="resetTableBusinessRowMenuState"
      @success="
        async () => {
          resetTableBusinessRowMenuState();
          await fetchStateWithOpeningChildBusiness();
        }
      "
    />
    <SelectDisplayInventoryYearMonthRangeModal
      :default-start-year-month="state.selectDisplayInventoryYearMonthRangeModalState.startYearMonth"
      :default-end-year-month="state.selectDisplayInventoryYearMonthRangeModalState.endYearMonth"
      :modal-state="state.selectDisplayInventoryYearMonthRangeModalState.isOpen"
      @on-next="setSelectDisplayInventoryYearMonthRangeModalState($event)"
      @on-back="state.selectDisplayInventoryYearMonthRangeModalState.isOpen = false"
    />
    <EditUnpaidOrderedOrderModal
      :modal-state="state.editUnpaidOrderedOrderModalState.isOpening"
      :unpaid-ordered-orders="state.editUnpaidOrderedOrderModalState.unpaidOrderedOrder"
      :execution-date="state.editUnpaidOrderedOrderModalState.executionDate"
      @close="state.editUnpaidOrderedOrderModalState.isOpening = false"
      @confirm="updateUnpaidOrderedOrder"
    />
    <CombinedOrderSettingModal
      :modal-state="state.combinedOrdersState.settingModalStatus"
      :default-desired-delivery-date="combinedOrderDefaultDesiredDeliveryDate"
      @close="state.combinedOrdersState.settingModalStatus = 'close'"
      @confirm="onConfirmCombinedOrder"
    />
    <OrderBasesModal :aggregate-business-id="orderBasesModalAggregateBusinessId" @close="resetTableBusinessRowMenuState" />
  </div>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';
</style>

<style lang="scss">
@import '@/InventoryControlModule/pages/scss/vue-datepicker-overrides.scss';
</style>
